import React from "react";
import styled from "styled-components";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import { Empty } from "antd";
import {
  HeatmapSeries,
  InfluenceDirection,
} from "../../types/linkageDashboardTypes";
import {
  Card,
  RowContainer,
  ElementContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { greyText, darkGreen, compassLightGreen } from "../../styles";
import { formatReadable } from "../../utils/formattingUtils";

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_LABEL_LENGTH = 24;
const truncateLabel = (label: string): string =>
  label.length > MAX_LABEL_LENGTH
    ? `${label.slice(0, MAX_LABEL_LENGTH)}…`
    : label;

const DIRECTION_LABELS: Record<InfluenceDirection, string> = {
  engagement_drives_vote: "Engagement → Vote",
  vote_triggers_engagement: "Vote → Engagement",
  bidirectional: "↔ Bidirectional",
  unknown: "Unknown",
};

const DIRECTION_LEGEND: Record<InfluenceDirection, string> = {
  engagement_drives_vote: "Engagement → Vote",
  vote_triggers_engagement: "Vote → Engagement",
  bidirectional: "↔ Bidirectional",
  unknown: "? Unknown",
};

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ChartContainer = styled.div<{ rowCount: number }>`
  height: ${({ rowCount }): number => Math.max(rowCount * 45 + 100, 220)}px;
  width: 100%;
  flex-shrink: 0;
`;

const ScrollableChartArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const LegendRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
  flex-shrink: 0;
`;

const LegendItem = styled.span`
  font-family: "Apercu";
  font-size: 11px;
  color: ${greyText};
`;

const ToggleContainer = styled.div`
  display: flex;
  background: #f2f4f7;
  border-radius: 6px;
  overflow: hidden;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 4px 12px;
  border: none;
  background: ${({ active }): string => (active ? "#153230" : "transparent")};
  color: ${({ active }): string => (active ? "white" : "#8b909a")};
  font-family: "Apercu";
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

// =============================================================================
// COMPONENT
// =============================================================================

interface MaterialEscalationChartProps {
  data: HeatmapSeries[];
  isLoading: boolean;
  showPercentage: boolean;
  onToggle: (showPct: boolean) => void;
}

export const MaterialEscalationChart: React.FC<
  MaterialEscalationChartProps
> = ({ data, isLoading, showPercentage, onToggle }) => {
  const isEmpty = !data || data.length === 0;

  return (
    <Card
      h="500px"
      isLoading={isLoading}
      style={{ display: "flex", flexDirection: "column", position: "relative" }}
    >
      <RowContainer mt={0} mb={8} jc="space-between" ai="center">
        <CompassParagraph fontSize={16} bold={true}>
          Material Topic Escalation
        </CompassParagraph>
        <ToggleContainer>
          <ToggleButton
            active={showPercentage}
            onClick={(): void => onToggle(true)}
          >
            %
          </ToggleButton>
          <ToggleButton
            active={!showPercentage}
            onClick={(): void => onToggle(false)}
          >
            #
          </ToggleButton>
        </ToggleContainer>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={12}>
        Influence direction between engagement and voting per SASB material
        topic
      </CompassParagraph>

      {isEmpty && !isLoading ? (
        <ElementContainer
          ai="center"
          jc="center"
          h="200px"
          w="100%"
          flexDirection="column"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No material escalation data available for the selected filters."
          />
        </ElementContainer>
      ) : (
        <ScrollableChartArea>
          <ChartContainer rowCount={data.length}>
            <ResponsiveHeatMap
              data={data}
              margin={{ top: 50, right: 20, bottom: 20, left: 180 }}
              axisTop={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: 0,
                format: (v: string): string =>
                  DIRECTION_LABELS[v as InfluenceDirection] ||
                  formatReadable(v),
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: -25,
                format: (v: string): string => truncateLabel(formatReadable(v)),
              }}
              colors={{
                type: "quantize",
                colors: ["#e6faf1", "#b0f5d8", "#70eebc", compassLightGreen],
                domain: [
                  0,
                  showPercentage ? 1 : (undefined as unknown as number),
                ],
              }}
              emptyColor="#F5F5F5"
              borderRadius={3}
              borderWidth={1}
              borderColor="#ffffff"
              enableLabels={true}
              label={(cell): string => {
                if (cell.value === null || cell.value === undefined) return "";
                return showPercentage
                  ? `${(Number(cell.value) * 100).toFixed(0)}%`
                  : String(cell.value);
              }}
              labelTextColor={{ from: "color", modifiers: [["darker", 2.5]] }}
              tooltip={({ cell }): JSX.Element => (
                <div
                  style={{
                    background: "white",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    fontFamily: "Apercu",
                    fontSize: 12,
                  }}
                >
                  <strong>{formatReadable(String(cell.serieId))}</strong>
                  <br />
                  {DIRECTION_LABELS[cell.data.x as InfluenceDirection] ||
                    formatReadable(String(cell.data.x))}
                  :{" "}
                  {showPercentage
                    ? `${(Number(cell.value) * 100).toFixed(1)}%`
                    : cell.value}
                </div>
              )}
              theme={{
                text: { fontFamily: "Apercu", fontSize: 11, fill: darkGreen },
                axis: {
                  ticks: {
                    text: {
                      fontFamily: "Apercu",
                      fontSize: 11,
                      fill: greyText,
                    },
                  },
                },
              }}
              animate={true}
              motionConfig="gentle"
            />
          </ChartContainer>
        </ScrollableChartArea>
      )}

      {/* Legend */}
      {!isEmpty && !isLoading && (
        <LegendRow>
          {(Object.keys(DIRECTION_LEGEND) as InfluenceDirection[]).map(
            (dir) => (
              <LegendItem key={dir}>{DIRECTION_LEGEND[dir]}</LegendItem>
            ),
          )}
        </LegendRow>
      )}
    </Card>
  );
};
